<template lang="pug">
el-dialog(
  custom-class="page__dialog privacy-policy-modal"
  visible
  :close-on-click-modal="false"
  :show-close="false")

  el-tabs(v-model="tab" slot="title")
    el-tab-pane(
      v-if="!policyAgreed"
      :name="TABS.POLICY")
      .title(slot="label") {{ POLICY_LABELS.POLICY }}
    el-tab-pane(
      v-if="!termAgreed"
      :name="TABS.TERM")
      .title(slot="label") {{ POLICY_LABELS.TERM }}
    el-tab-pane(
      v-if="!termOfServiceAgreed"
      :name="TABS.TERM_OF_SERVICE")
      .title(slot="label") {{ `${roleName} ${POLICY_LABELS.TERM_OF_SERVICE}` }}

  loading(v-if="loading")
  article.page__article.with-scroller(v-else-if="data[tab]")
    vue-markdown(
      :source="data[tab]"
      :linkify="false")

  el-checkbox.mt-3(v-if="!policyAgreed" v-model="read[TABS.POLICY]") I have read, understood, and agree to the Privacy Policy.
  el-checkbox.mt-1(v-if="!termAgreed" v-model="read[TABS.TERM]") I have read, understood, and agree to the Site Terms of Use.
  el-checkbox.mt-1(v-if="!termOfServiceAgreed" v-model="read[TABS.TERM_OF_SERVICE]") I have read, understood, and agree to the {{ roleName }} Terms of Services.

  el-alert.mt-2(
    type="warning"
    :closable="false")
    span(v-html="helpText")

  .page__dialog-footer(slot="footer")
    el-button(
      type="primary"
      @click="accept"
      :disabled="disabledSubmit || submitting")
      i.bx.bx-loader.bx-spin.bx-5.icon.mr(v-if="submitting")
      i.bx.bx-check-circle.bx-5.icon.mr(v-else)
      span I agree
</template>

<script>
import { startCase } from 'lodash'
import VueMarkdown from 'vue-markdown'
import { mapGetters } from 'vuex'
import Loading from '~/components/Loading'
import { POLICY_LABELS } from '~/settings'

const TABS = {
  POLICY: 'POLICY',
  TERM: 'TERM',
  TERM_OF_SERVICE: 'TERM_OF_SERVICE'
}

export default {
  components: {
    VueMarkdown,
    Loading
  },

  props: {
    api: {
      type: Object,
      required: true
    },
    policyAgreed: {
      type: Boolean,
      default: false
    },
    termAgreed: {
      type: Boolean,
      default: false
    },
    termOfServiceAgreed: {
      type: Boolean,
      default: false
    }
  },

  data () {
    const tab = !this.policyAgreed
      ? TABS.POLICY
      : !this.termAgreed
        ? TABS.TERM
        : TABS.TERM_OF_SERVICE

    return {
      TABS,
      POLICY_LABELS,
      tab,
      loading: false,
      submitting: false,
      data: {},
      read: {
        [TABS.POLICY]: this.policyAgreed,
        [TABS.TERM]: this.termAgreed,
        [TABS.TERM_OF_SERVICE]: this.termOfServiceAgreed
      }
    }
  },

  computed: {
    ...mapGetters({
      role: 'authentication/role',
      apiRole: 'authentication/apiRole'
    }),

    roleName () {
      const role = {
        broker: 'vendor',
        head_buyer: 'buyer'
      }[this.role] || this.role

      return startCase(role)
    },

    disabledSubmit () {
      return Object.values(this.read).some(e => !e)
    },

    helpText () {
      const policyList = []

      if (!this.policyAgreed) policyList.push(POLICY_LABELS.POLICY)
      if (!this.termAgreed) policyList.push(POLICY_LABELS.TERM)
      if (!this.termOfServiceAgreed) policyList.push(`${this.roleName} ${POLICY_LABELS.TERM_OF_SERVICE}`)

      const policyText = policyList.length > 1
        ? `${policyList.slice(0, -1).join(', ')} and ${policyList.at(-1)}`
        : policyList[0] || ''

      return `We have updated our ${policyText}.<br/> By checking “I agree”, I am accepting the updated terms.`
    }
  },

  mounted () {
    return this.fetch()
  },

  methods: {
    fetch () {
      this.loading = true

      const promises = [
        this.policyAgreed ? Promise.resolve() : this.api.common.getPrivacyPolicy(),
        this.termAgreed ? Promise.resolve() : this.api.common.getTermOfUse(),
        this.termOfServiceAgreed ? Promise.resolve() : this.api[this.apiRole].getTermsOfService()
      ]

      return Promise.all(promises)
        .then(([policyRes, termRes, tosRes]) => {
          this.data = {
            [TABS.POLICY]: policyRes?.content || '',
            [TABS.TERM]: termRes?.content || '',
            [TABS.TERM_OF_SERVICE]: tosRes?.content || ''
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    accept () {
      this.submitting = true

      return this.api.common
        .acceptAgreements({
          agree_to_policies: true,
          agree_to_terms: true,
          agree_to_terms_of_services: true
        })
        .then(() => {
          this.$emit('done', true)
          this.$emit('close')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .privacy-policy-modal {
    max-width: 720px;
  }
}
.title {
  font-weight: 800;
  font-size: 18px;
}
article {
  max-height: 480px;
  overflow-y: scroll;
  overflow-x: hidden;
  ::v-deep {
    h1, h2, h3 {
      margin: .5em 0 .25em 0;
    }
    p {
      margin: .75em 0 0 0;
      line-height: 1.5em;
    }
  }
}
</style>
